import * as React from 'react';
import { classNames } from 'utils/ui/css';

export interface ICrossfadeProps {
  /**
   * Identity of the current content. A change triggers a crossfade: the previous content stays
   * mounted as an "outgoing" layer fading out while the new content fades in over it. While the
   * key is unchanged the latest `children` render in place (so live `<video>` ticks / mute
   * changes never remount the tile).
   */
  transitionKey: string;
  /**
   * Fade duration in ms used to drop the outgoing layer. MUST match the CSS layer transition
   * duration (the `.ex3d-crossfade__layer` token). Defaults to 240 (= --groupui-vwgroup-ref-duration-240).
   */
  durationMs?: number;
  className?: string;
  children: React.ReactNode;
}

type Layer = { id: number; key: string; node: React.ReactNode };

/**
 * Crossfades between successive children. Used for the VOIP self tile swapping between the standby
 * loopback and the dealer's own camera on takeover / step-down (AVS-4302) so there is no black gap
 * or hard cut. The visual fade lives in CSS (`.ex3d-crossfade`); this component only manages which
 * layers are mounted. Respects `prefers-reduced-motion` via the stylesheet.
 */
export const Crossfade: React.FC<ICrossfadeProps> = ({ transitionKey, durationMs = 240, className, children }) => {
  const idRef = React.useRef(0);
  const [layers, setLayers] = React.useState<Layer[]>(() => [{ id: 0, key: transitionKey, node: children }]);

  // Keep the current (top) layer's node live; on a key change append a NEW top layer so the old
  // one becomes an outgoing layer (still mounted, fading out).
  React.useEffect(() => {
    setLayers((prev) => {
      const top = prev[prev.length - 1];
      if (top.key === transitionKey) {
        const next = prev.slice();
        next[next.length - 1] = { ...top, node: children };
        return next;
      }
      idRef.current += 1;
      return [...prev, { id: idRef.current, key: transitionKey, node: children }];
    });
  }, [transitionKey, children]);

  // When a NEW top layer appears (topId changes), drop every older layer once the fade completes.
  // Keying the effect on `topId` (not `layers`) means in-place node updates during the fade do not
  // reset the timer, so the outgoing layer is removed on schedule.
  const topId = layers[layers.length - 1]?.id;
  React.useEffect(() => {
    if (layers.length <= 1) return undefined;
    const timer = setTimeout(() => {
      setLayers((prev) => prev.filter((layer) => layer.id === topId));
    }, durationMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on topId, not layers
  }, [topId, durationMs]);

  return (
    <div className={classNames('ex3d-crossfade', className)}>
      {layers.map((layer, index) => {
        const isCurrent = index === layers.length - 1;
        return (
          <div
            key={layer.id}
            className="ex3d-crossfade__layer"
            data-state={isCurrent ? 'in' : 'out'}
            // The outgoing layer is a fading-out duplicate of stale content — hide it from
            // assistive tech so only the current content is announced during the crossfade.
            aria-hidden={isCurrent ? undefined : true}
          >
            {layer.node}
          </div>
        );
      })}
    </div>
  );
};

export default Crossfade;
